/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import * as React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// HOC to provide router props to class components in React Router v7
export interface WithRouterProps {
    navigate: ReturnType<typeof useNavigate>;
    params: Record<string, string | undefined>;
    location: ReturnType<typeof useLocation>;
}

export function withRouter<P extends WithRouterProps>(
    Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof WithRouterProps>> {
    return (props: Omit<P, keyof WithRouterProps>) => {
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        return (
            <Component
                {...(props as P)}
                navigate={navigate}
                params={params}
                location={location}
            />
        );
    };
}

// For backward compatibility - match old Router v4 interface
export interface RouteComponentPropsCompat {
    history: {
        push: (path: string) => void;
        replace: (path: string) => void;
        goBack: () => void;
    };
    match: {
        params: Record<string, string | undefined>;
    };
    location: ReturnType<typeof useLocation>;
}

export function withRouterCompat<P extends RouteComponentPropsCompat>(
    Component: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof RouteComponentPropsCompat>> {
    return (props: Omit<P, keyof RouteComponentPropsCompat>) => {
        const navigate = useNavigate();
        const params = useParams();
        const location = useLocation();

        const history = {
            push: (path: string) => navigate(path),
            replace: (path: string) => navigate(path, { replace: true }),
            goBack: () => navigate(-1),
        };

        const match = {
            params,
        };

        return (
            <Component
                {...(props as P)}
                history={history}
                match={match}
                location={location}
                navigate={navigate}
            />
        );
    };
}
